import {DependencyList, Ref, useEffect, useRef} from 'react';
import {BroadcastEventManager} from './broadcast/broadcast-event-manager';
import {AggregatePattern, Aggregation} from './common/aggregate';
import {Constructor, EventHandler} from './common/types';
import {EventBusManager} from './local/event-bus-manager';

function setRef<T>(ref: T, target: Ref<T>): void {
	if (target) {
		if (typeof target === 'function') {
			target(ref);
			return;
		}

		if (typeof target === 'object') {
			(target as any).current = ref;
		}
	}
}

export function useEventEffect<T>(event: Constructor<T>, handler: EventHandler<T>, deps?: DependencyList): void {
	//Optimization.
	const handlerRef = useRef<EventHandler<T>>();
	setRef(handler, handlerRef);

	useEffect(() => {
		const manager = new EventBusManager();
		manager.subscribe(event, (args: T, context) => handlerRef.current?.(args, context));
		return () => manager.destroy();
	}, deps || []);
}

export function useBroadcastEventEffect<T>(event: Constructor<T>, handler: EventHandler<T>): void {
	//Optimization.
	const handlerRef = useRef<EventHandler<T>>();
	setRef(handler, handlerRef);

	useEffect(() => {
		const manager = new BroadcastEventManager();
		manager.subscribe(event, (args: T, context) => handlerRef.current?.(args, context));
		return () => manager.destroy();
	}, []);
}

export function useEventAggregationEffect(pattern: AggregatePattern, handler: EventHandler<Aggregation>) {
	const handlerRef = useRef<EventHandler<Aggregation>>();
	setRef(handler, handlerRef);

	useEffect(() => {
		const manager = new BroadcastEventManager();
		manager.aggregate(pattern).subscribe((args) => handlerRef.current?.(args));
		return () => manager.destroy();
	}, [pattern]);
}
